<script setup lang="ts">
    import { computed } from 'vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { AlertTypes } from '@shared/components/BsAlerts';
    import { Bar } from 'vue-chartjs';
    import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip } from 'chart.js';
    import { applyChartDefaults } from '@shared/modules/chartjs-defaults';
    import { chartColor } from '@shared/modules/chart-colors';
    import type { UserAgentStats } from '../../interfaces';

    ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip);
    applyChartDefaults();

    const props = defineProps<{
        mailClients: UserAgentStats | null;
    }>();

    function fmt(val: number): string {
        return val > 0 && val < 1 ? val.toFixed(1) : Math.round(val).toString();
    }

    const chartData = computed(() => {
        if (!props.mailClients?.UserAgents.length) return { labels: [], datasets: [] };
        return {
            labels: props.mailClients.UserAgents.map((ua) => [ua.Name, `${fmt(ua.ContactCount)} (${fmt(ua.ContactPercentage)}%)`]),
            datasets: [
                {
                    data: props.mailClients.UserAgents.map((ua) => parseFloat(fmt(ua.ContactPercentage))),
                    backgroundColor: chartColor('primary'),
                },
            ],
        };
    });

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: { callbacks: { label: () => '' } },
        },
        scales: {
            x: {
                grid: { display: false },
                ticks: { font: { weight: 'bold' as const } },
            },
            y: {
                min: 0,
                max: 100,
                grid: { color: chartColor('grid') },
                ticks: { callback: (value: number | string) => value + '%' },
            },
        },
    };
</script>

<template>
    <div class="print-block" v-if="mailClients">
        <h2 class="h3">
            {{ $Resources.IssueStatisticsUserAgentsTab }}
            <button
                type="button"
                class="popover-trigger popover-trigger-lg"
                v-popover-info="{ heading: $Resources.IssueStatisticsUserAgentsTab, body: $Resources.IssueStatisticsUserAgentsIntroduction }">
                <span class="glyphicon glyphicon-info-sign" aria-hidden="true"></span>
            </button>
        </h2>

        <BsAlert
            :show="mailClients.UserAgents.length === 0"
            :type="AlertTypes.Info"
            :dismissible="false"
            :content="$Resources.IssueStatisticsNoDeviceDataMessage" />

        <template v-if="mailClients.UserAgents.length > 0">
            <div class="stat-highlights panel panel-default">
                <div class="row no-gutter d-flex flex-wrap">
                    <div class="flex-col col-md-3 col-sm-6 col-xs-12 col-print-6">
                        <div class="stat-highlights-body ug-bars">
                            <p>{{ $Resources.IssueStatisticsUserAgentsDesktopPercentageLabel }}</p>
                            <p class="value">{{ Math.round(mailClients.DesktopPercentage) }}%</p>
                            <div class="bar">
                                <span class="bar-value" :style="{ width: mailClients.DesktopPercentage + '%' }"></span>
                            </div>
                        </div>
                    </div>
                    <div class="flex-col col-md-3 col-sm-6 col-xs-12 col-print-6">
                        <div class="stat-highlights-body ug-bars">
                            <p>{{ $Resources.IssueStatisticsUserAgentsMobilePercentageLabel }}</p>
                            <p class="value">{{ Math.round(mailClients.MobilePercentage) }}%</p>
                            <div class="bar">
                                <span class="bar-value" :style="{ width: mailClients.MobilePercentage + '%' }"></span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="chart-container chart-container-panel h-400">
                <Bar :data="chartData" :options="chartOptions" />
            </div>
        </template>
    </div>
</template>
